import { IFileSink } from '@/contracts/core/file/IFileSink'
import { IZipWriter } from '@/contracts/core/file/IZipWriter'
import { crc32Table } from '@/helpers/crc32'

/** Размер или смещение, начиная с которого поле не помещается в 32 бита: нужен ZIP64 */
export const ZIP64_LIMIT = 0xFFFFFFFF
const FLAG_DATA_DESCRIPTOR = 0x0008
const FLAG_UTF8 = 0x0800
const VERSION_STORE = 20
const VERSION_ZIP64 = 45
const SIGNATURE_LOCAL = 0x04034b50
const SIGNATURE_DESCRIPTOR = 0x08074b50
const SIGNATURE_CENTRAL = 0x02014b50
const SIGNATURE_END = 0x06054b50
const SIGNATURE_ZIP64_END = 0x06064b50
const SIGNATURE_ZIP64_LOCATOR = 0x07064b50
const ZIP64_EXTRA_ID = 0x0001

interface IEntry {
  name: Uint8Array
  size: number
  crc: number
  offset: number
  time: number
  date: number
  zip64: boolean
}

/** Дата и время DOS: секунды с точностью до двух, годы от 1980 */
function dosDateTime(now: Date): { time: number, date: number } {
  const year = Math.max(now.getFullYear(), 1980)
  return {
    time: (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1),
    date: ((year - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate(),
  }
}

/** Буфер записей little-endian для заголовков архива */
class Builder {
  private view: DataView
  private bytes: Uint8Array
  private position = 0

  constructor(size: number) {
    this.bytes = new Uint8Array(size)
    this.view = new DataView(this.bytes.buffer)
  }

  u16(value: number): this {
    this.view.setUint16(this.position, value, true)
    this.position += 2
    return this
  }

  u32(value: number): this {
    this.view.setUint32(this.position, value >>> 0, true)
    this.position += 4
    return this
  }

  u64(value: number): this {
    this.view.setBigUint64(this.position, BigInt(value), true)
    this.position += 8
    return this
  }

  raw(value: Uint8Array): this {
    this.bytes.set(value, this.position)
    this.position += value.length
    return this
  }

  done(): Uint8Array {
    return this.bytes.subarray(0, this.position)
  }
}

/**
 * Архив пишется потоком: заголовок файла, байты, дескриптор с CRC32 и размером, в конце —
 * центральный каталог. Порог ZIP64 задаётся для тестов: боевое значение — ZIP64_LIMIT.
 */
export class ZipWriter implements IZipWriter {
  /** Точный размер архива из файлов с такими путями и размерами: для Content-Length потока */
  static archiveSize(files: { path: string, size: number }[], zip64From: number = ZIP64_LIMIT): number {
    let offset = 0
    let directory = 0
    let anyZip64 = false
    files.forEach((file) => {
      const name = new TextEncoder().encode(file.path).length
      const zip64 = file.size >= zip64From || offset >= zip64From
      anyZip64 = anyZip64 || zip64
      offset += 30 + name + (zip64 ? 20 : 0) + file.size + (zip64 ? 24 : 16)
      directory += 46 + name + (zip64 ? 28 : 0)
    })
    const zip64End = anyZip64 || files.length >= 0xFFFF || offset >= zip64From || directory >= zip64From

    return offset + directory + (zip64End ? 56 + 20 : 0) + 22
  }

  private entries: IEntry[] = []
  private offset = 0
  private closed = false
  private table = crc32Table()

  constructor(private sink: IFileSink, private zip64From: number = ZIP64_LIMIT, private now: () => Date = () => new Date()) {}

  async addFile(path: string, size: number, read: (offset: number) => Promise<Uint8Array>): Promise<void> {
    this.throwIfClosed()
    const entry: IEntry = { name: new TextEncoder().encode(path), size, crc: 0, offset: this.offset, time: 0, date: 0, zip64: size >= this.zip64From || this.offset >= this.zip64From }
    Object.assign(entry, dosDateTime(this.now()))
    await this.emit(this.localHeader(entry))
    let crc = 0xFFFFFFFF
    let position = 0
    while (position < size) {
      const chunk = await read(position)
      if (chunk.length === 0) {
        throw new Error('zip: read gave no bytes at ' + position)
      }
      crc = this.crcUpdate(crc, chunk)
      await this.emit(chunk)
      position += chunk.length
    }
    entry.crc = (crc ^ 0xFFFFFFFF) >>> 0
    await this.emit(this.descriptor(entry))
    this.entries.push(entry)
  }

  async close(): Promise<void> {
    this.throwIfClosed()
    this.closed = true
    const directoryOffset = this.offset
    for (const entry of this.entries) {
      await this.emit(this.centralHeader(entry))
    }
    const directorySize = this.offset - directoryOffset
    const zip64 = this.entries.some((entry) => entry.zip64) || this.entries.length >= 0xFFFF || directoryOffset >= this.zip64From || directorySize >= this.zip64From
    if (zip64) {
      const zip64EndOffset = this.offset
      await this.emit(this.zip64End(directoryOffset, directorySize))
      await this.emit(this.zip64Locator(zip64EndOffset))
    }
    await this.emit(this.end(directoryOffset, directorySize, zip64))
    await this.sink.close()
  }

  private async emit(bytes: Uint8Array): Promise<void> {
    await this.sink.write(this.offset, bytes)
    this.offset += bytes.length
  }

  private localHeader(entry: IEntry): Uint8Array {
    const extra = entry.zip64 ? new Builder(20).u16(ZIP64_EXTRA_ID).u16(16).u64(0).u64(0).done() : new Uint8Array(0)
    const sizes = entry.zip64 ? ZIP64_LIMIT : 0

    return new Builder(30 + entry.name.length + extra.length)
      .u32(SIGNATURE_LOCAL)
      .u16(entry.zip64 ? VERSION_ZIP64 : VERSION_STORE)
      .u16(FLAG_DATA_DESCRIPTOR | FLAG_UTF8)
      .u16(0)
      .u16(entry.time)
      .u16(entry.date)
      .u32(0)
      .u32(sizes)
      .u32(sizes)
      .u16(entry.name.length)
      .u16(extra.length)
      .raw(entry.name)
      .raw(extra)
      .done()
  }

  private descriptor(entry: IEntry): Uint8Array {
    const builder = new Builder(24).u32(SIGNATURE_DESCRIPTOR).u32(entry.crc)
    if (entry.zip64) {
      return builder.u64(entry.size).u64(entry.size).done()
    }
    return builder.u32(entry.size).u32(entry.size).done()
  }

  private centralHeader(entry: IEntry): Uint8Array {
    const extra = entry.zip64 ? new Builder(28).u16(ZIP64_EXTRA_ID).u16(24).u64(entry.size).u64(entry.size).u64(entry.offset).done() : new Uint8Array(0)
    const size = entry.zip64 ? ZIP64_LIMIT : entry.size
    const offset = entry.zip64 ? ZIP64_LIMIT : entry.offset

    return new Builder(46 + entry.name.length + extra.length)
      .u32(SIGNATURE_CENTRAL)
      .u16(entry.zip64 ? VERSION_ZIP64 : VERSION_STORE)
      .u16(entry.zip64 ? VERSION_ZIP64 : VERSION_STORE)
      .u16(FLAG_DATA_DESCRIPTOR | FLAG_UTF8)
      .u16(0)
      .u16(entry.time)
      .u16(entry.date)
      .u32(entry.crc)
      .u32(size)
      .u32(size)
      .u16(entry.name.length)
      .u16(extra.length)
      .u16(0)
      .u16(0)
      .u16(0)
      .u32(0)
      .u32(offset)
      .raw(entry.name)
      .raw(extra)
      .done()
  }

  private zip64End(directoryOffset: number, directorySize: number): Uint8Array {
    return new Builder(56)
      .u32(SIGNATURE_ZIP64_END)
      .u64(44)
      .u16(VERSION_ZIP64)
      .u16(VERSION_ZIP64)
      .u32(0)
      .u32(0)
      .u64(this.entries.length)
      .u64(this.entries.length)
      .u64(directorySize)
      .u64(directoryOffset)
      .done()
  }

  private zip64Locator(zip64EndOffset: number): Uint8Array {
    return new Builder(20).u32(SIGNATURE_ZIP64_LOCATOR).u32(0).u64(zip64EndOffset).u32(1).done()
  }

  private end(directoryOffset: number, directorySize: number, zip64: boolean): Uint8Array {
    const count = zip64 ? 0xFFFF : this.entries.length

    return new Builder(22)
      .u32(SIGNATURE_END)
      .u16(0)
      .u16(0)
      .u16(count)
      .u16(count)
      .u32(zip64 ? ZIP64_LIMIT : directorySize)
      .u32(zip64 ? ZIP64_LIMIT : directoryOffset)
      .u16(0)
      .done()
  }

  private crcUpdate(crc: number, bytes: Uint8Array): number {
    let value = crc
    for (let i = 0; i < bytes.length; i++) {
      value = this.table[(value ^ bytes[i]) & 0xFF] ^ (value >>> 8)
    }
    return value
  }

  private throwIfClosed(): void {
    if (this.closed) {
      throw new Error('zip: archive is closed')
    }
  }
}
